import { I18nManager, Animated, Easing, StyleSheet, View } from 'react-native';
import React from 'react';
import PropTypes from 'prop-types';

import { OPEN_ANIM_DURATION, CLOSE_ANIM_DURATION, USE_NATIVE_DRIVER } from '../constants.js';

const popoverPadding = 7;
const anchorSize = 15;
const anchorHyp = Math.sqrt(anchorSize * anchorSize + anchorSize * anchorSize);
const anchorOffset = (anchorHyp + anchorSize) / 2 - popoverPadding;
const anchorEdgeInset = 8;

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function clampAnchorOffset(offset, placement, layouts) {
  const optionsLayout = layouts?.optionsLayout;
  if (!optionsLayout) {
    return offset;
  }

  const optionsWidth = optionsLayout.width + popoverPadding * 2;
  const optionsHeight = optionsLayout.height + popoverPadding * 2;
  const maxHorizontalOffset = Math.max(0, optionsWidth / 2 - anchorSize / 2 - anchorEdgeInset);
  const maxVerticalOffset = Math.max(0, optionsHeight / 2 - anchorSize / 2 - anchorEdgeInset);

  if (placement === 'top' || placement === 'bottom') {
    return clamp(offset, -maxHorizontalOffset, maxHorizontalOffset);
  }
  if (placement === 'left' || placement === 'right') {
    return clamp(offset, -maxVerticalOffset, maxVerticalOffset);
  }
  return offset;
}

// left/top placement
function axisNegativeSideProperties({ oDim, tPos }) {
  return { position: tPos - oDim };
}

// right/bottom placement
function axisPositiveSideProperties({ tPos, tDim }) {
  // substract also anchor placeholder from the beginning
  return { position: tPos + tDim - anchorSize };
}

// computes offsets (off screen overlap) of popover when trying to align it to the center
function centeringProperties({ oDim, wDim, tPos, tDim }) {
  const center = Math.round(tPos + (tDim / 2));
  const leftOffset = (oDim / 2) - center;
  const rightOffset = center + (oDim / 2) - wDim;
  return { center, leftOffset, rightOffset };
}

/**
 * Computes position and offset of popover when trying to align it to the triger center.
 * It consideres window boundaries.
 * Returns object with keys:
 *   - position: <Number> Absolute position - top/left,
 *   - offset: <Number> window overlapping size if window boundaries were not considered
 */
function axisCenteredPositionProperties(options) {
  const { oDim, wDim } = options;
  const { center, leftOffset, rightOffset } = centeringProperties(options);
  if (leftOffset > 0 || rightOffset > 0) {
    // right/bottom position is better
    if (leftOffset < rightOffset) {
      return { offset: rightOffset, position: wDim - oDim };
    }
    // left/top position is better
    if (rightOffset < leftOffset) {
      return { offset: -leftOffset, position: 0 };
    }
  }
  // centered position
  return { offset: 0, position: center - oDim / 2 };
}

/* Evaluate centering placement */
function getCenteringPrice(options) {
  const { leftOffset, rightOffset } = centeringProperties(options);
  // TODO: currently shifted popovers have higher price,
  // popover shift could be taken into account with the same price
  return Math.max(0, leftOffset) + Math.max(0, rightOffset);
}

/* Evaluate top placement */
function getTopPrice(hOptions, vOptions) {
  const centerOffset = getCenteringPrice(vOptions)
  const sideOffset = Math.max(0, hOptions.oDim - hOptions.tPos)
  return centerOffset + sideOffset
}

/* Evaluate bottom placement */
function getBottomPrice(hOptions, vOptions) {
  const centerOffset = getCenteringPrice(vOptions)
  const sideOffset = Math.max(0, hOptions.tPos + hOptions.tDim + hOptions.oDim - hOptions.wDim)
  return centerOffset + sideOffset
}

/* Evaluate left placement */
function getLeftPrice(hOptions, vOptions) {
  const centerOffset = getCenteringPrice(hOptions)
  const sideOffset = Math.max(0, vOptions.oDim - vOptions.tPos)
  return centerOffset + sideOffset
}

/* Evaluate right placement */
function getRightPrice(hOptions, vOptions) {
  const centerOffset = getCenteringPrice(hOptions)
  const sideOffset = Math.max(0, vOptions.tPos + vOptions.tDim + vOptions.oDim - vOptions.wDim)
  return centerOffset + sideOffset
}

function getStartPosKey(isRTL) {
  return isRTL ? 'right' : 'left';
}

function topProperties(hOptions, vOptions, isRTL) {
  const centered = axisCenteredPositionProperties(vOptions);
  const side = axisNegativeSideProperties(hOptions);

  if (side.position < 50) {
    return bottomProperties(hOptions, vOptions, isRTL)
  }
  return {
    position: {
      top: side.position,
      [getStartPosKey(isRTL)]: centered.position,
    },
    offset: centered.offset,
    placement: 'top',
  };

  const screenWidth = vOptions.wDim//屏幕宽
  const menuWidth = vOptions.oDim//菜单宽
  const width = vOptions.tDim//组件宽

  const screenHeight = hOptions.wDim//屏幕高
  const menuHeight = hOptions.oDim//菜单高
  const height = hOptions.tDim//组件高
  const tPos = Math.abs(hOptions.tPos)//距离顶部位置
}

function bottomProperties(hOptions, vOptions, isRTL) {
  const centered = axisCenteredPositionProperties(vOptions);
  const side = axisPositiveSideProperties(hOptions);

  if (side.position+hOptions.oDim > hOptions.wDim) {
    return centerProperties(hOptions, vOptions)
  }

  return {
    position: {
      top: side.position,
      [getStartPosKey(isRTL)]: centered.position,
    },
    offset: centered.offset,
    placement: 'bottom',
  };
}

function rightProperties(hOptions, vOptions, isRTL) {
  const centered = axisCenteredPositionProperties(hOptions);
  const side = axisPositiveSideProperties(vOptions);
  return {
    position: {
      top: centered.position,
      [getStartPosKey(isRTL)]: side.position,
    },
    offset: centered.offset,
    placement: 'right',
  };
}

function leftProperties(hOptions, vOptions, isRTL) {
  const centered = axisCenteredPositionProperties(hOptions);
  const side = axisNegativeSideProperties(vOptions);
  return {
    position: {
      top: centered.position,
      [getStartPosKey(isRTL)]: side.position,
    },
    offset: centered.offset,
    placement: 'left',
  };
}

function centerProperties(hOptions, vOptions) {
  const centered = axisCenteredPositionProperties(hOptions);
  return {
    position: {
      top: (hOptions.wDim - hOptions.oDim) / 2,
      start: (vOptions.wDim - vOptions.oDim) / 2,
    },
    offset: centered.offset,
    placement: 'top',
  };
}

// maps placement to function which computes correct properties
const propertiesByPlacement = {
  top: topProperties,
  bottom: bottomProperties,
  left: leftProperties,
  right: rightProperties,
};

/**
 * Computes properties needed for drawing popover.
 * Returns object with keys:
 *   - position: <Object> { top: Number, left: Number } - popover absolute position
 *   - placement: <Enum> top|left|top|bottom - position to the trigger
 *   - offset: <Number> value by which must be anchor shifted
 */
export function computeProperties(
  { windowLayout, triggerLayout, optionsLayout },
  placement,
  preferredPlacement,
  isRTL,
) {
  const { x: wX, y: wY, width: wWidth, height: wHeight } = windowLayout;
  const { x: tX, y: tY, height: tHeight, width: tWidth } = triggerLayout;
  const { height: oHeight, width: oWidth } = optionsLayout;
  const hOptions = {
    oDim: oHeight + popoverPadding * 2,
    wDim: wHeight,
    tPos: tY - wY,
    tDim: tHeight,
  };
  const vOptions = {
    oDim: oWidth + popoverPadding * 2,
    wDim: wWidth,
    tPos: tX - wX,
    tDim: tWidth,
  };
  if (placement !== 'auto' && propertiesByPlacement[placement]) {
    return propertiesByPlacement[placement](hOptions, vOptions, isRTL)
  }

  const prices = {
    top: getTopPrice(hOptions, vOptions),
    bottom: getBottomPrice(hOptions, vOptions),
    right: getRightPrice(hOptions, vOptions),
    left: getLeftPrice(hOptions, vOptions),
  };
  const bestPrice = Object.values(prices).sort((a, b) => a - b)[0]
  const bestPlacement = prices[preferredPlacement] === bestPrice
    ? preferredPlacement
    : Object.keys(prices).find(pl => prices[pl] === bestPrice)
  return propertiesByPlacement[bestPlacement](hOptions, vOptions, isRTL)
}

export default class Popover extends React.Component {

  constructor(props) {
    super(props);
    this.state = {
      scaleAnim: new Animated.Value(0.1),
    };
    this._lockedArrowOffset = null;
    this._lockedPlacement = null;
  }

  componentDidMount() {
    Animated.timing(this.state.scaleAnim, {
      duration: this.props.openAnimationDuration !== undefined ? this.props.openAnimationDuration : OPEN_ANIM_DURATION,
      toValue: 1,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: USE_NATIVE_DRIVER,
    }).start();
  }

  close() {
    return new Promise(resolve => {
      Animated.timing(this.state.scaleAnim, {
        duration: this.props.closeAnimationDuration !== undefined ? this.props.closeAnimationDuration : CLOSE_ANIM_DURATION,
        toValue: 0,
        easing: Easing.in(Easing.cubic),
        useNativeDriver: USE_NATIVE_DRIVER,
      }).start(resolve);
    });
  }

  render() {
    const {
      style,
      children,
      layouts,
      anchorPosition,
      anchorStyle,
      preferredPlacement,
      openAnimationDuration,
      closeAnimationDuration,
      placement: userPlacement,
      ...other
    } = this.props;
    const isRTL = I18nManager.isRTL;
    const animation = {
      transform: [{ scale: this.state.scaleAnim }],
      opacity: this.state.scaleAnim,
    };
    const adjustedLayouts = anchorPosition && layouts && layouts.triggerLayout
      ? {
        ...layouts,
        triggerLayout: {
          ...layouts.triggerLayout,
          x: (layouts.windowLayout?.x || 0) + anchorPosition.x,
          y: (layouts.windowLayout?.y || 0) + anchorPosition.y,
          width: 1,
          height: 1,
        },
      }
      : layouts;

    const { position, placement, offset } = computeProperties(
      adjustedLayouts,
      userPlacement,
      preferredPlacement,
      isRTL,
    );
    const clampedOffset = clampAnchorOffset(offset, placement, adjustedLayouts);
    if (this._lockedArrowOffset === null || this._lockedPlacement !== placement) {
      this._lockedArrowOffset = clampedOffset;
      this._lockedPlacement = placement;
    }
    const finalOffset = this._lockedArrowOffset;
    return (
      <Animated.View
        style={[
          styles.animated,
          animation,
          position,
          getContainerStyle({ placement, isRTL }),
        ]}
        pointerEvents="box-none"
      >
        <View
          style={[
            styles.anchor,
            dynamicAnchorStyle({ placement, offset: finalOffset, isRTL }),
            anchorStyle,
          ]}
        />
        <View {...other} style={[styles.options, style]}>
          {children}
        </View>
      </Animated.View>
    );
  }

}

Popover.propTypes = {
  anchorPosition: PropTypes.shape({
    x: PropTypes.number,
    y: PropTypes.number,
  }),
  anchorStyle: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.number,
    PropTypes.array,
  ]),
  placement: PropTypes.oneOf(['auto', 'top', 'right', 'bottom', 'left']),
  preferredPlacement: PropTypes.oneOf(['top', 'right', 'bottom', 'left']),
  openAnimationDuration: PropTypes.number,
  closeAnimationDuration: PropTypes.number,
};

Popover.defaultProps = {
  preferredPlacement: 'top',
  placement: 'auto',
};

const getContainerStyle = ({ placement, isRTL }) => ({
  left: {
    flexDirection: isRTL ? 'row' : 'row-reverse',
  },
  right: {
    flexDirection: isRTL ? 'row-reverse' : 'row',
  },
  top: {
    flexDirection: 'column-reverse',
  },
  bottom: {
    flexDirection: 'column',
  },
})[placement]

const dynamicAnchorStyle = ({ offset, placement, isRTL }) => {
  const start = getStartPosKey(isRTL);
  switch (placement) {
    case 'right':
      return {
        top: offset,
        transform: [
          { translateX: anchorOffset },
          { rotate: '45deg' },
        ],
      };
    case 'left':
      return {
        top: offset,
        transform: [
          { translateX: -anchorOffset },
          { rotate: '45deg' },
        ],
      };
    case 'top':
      return {
        [start]: offset,
        transform: [
          { translateY: -anchorOffset },
          { rotate: '45deg' },
        ],
      };
    case 'bottom':
      return {
        [start]: offset,
        transform: [
          { translateY: anchorOffset },
          { rotate: '45deg' },
        ],
      };
  }
}

export const styles = StyleSheet.create({
  animated: {
    padding: popoverPadding,
    backgroundColor: 'transparent',
    position: 'absolute',
    alignItems: 'center',
  },
  options: {
    borderRadius: 2,
    minWidth: anchorHyp,
    minHeight: anchorHyp,
    backgroundColor: 'white',

    // Shadow only works on iOS.
    shadowColor: 'black',
    shadowOpacity: 0.3,
    shadowOffset: { width: 3, height: 3 },
    shadowRadius: 4,

    // This will elevate the view on Android, causing shadow to be drawn.
    elevation: 5,
  },
  anchor: {
    width: anchorSize,
    height: anchorSize,
    backgroundColor: 'white',
    elevation: 5,
  },
});
